import { z } from 'zod';
import { MAX_TRANSLATION_BATCH_BLOCKS } from './translation-text.js';
/**
 * Bản dịch theo khối của tài liệu `.chilldoc` — APM-BIL-a, docs/plan-chilldoc-bilingual.md §2, §3.
 *
 * Chế độ Song ngữ dịch **từng khối** (đoạn, tiêu đề, cả danh sách, cả bảng) khi người dùng bấm.
 * Bản dịch nằm ở máy chủ, chung cho tài liệu: ai xem được tài liệu cũng thấy bản đã dịch. Không
 * đổi tệp, không tạo phiên bản. Bản AI ở `ai_suggested` (luật #6) — chỉ hiển thị.
 *
 * Luật #3: API chỉ nhận chữ của **một khối hay một trang khối** (tổng ≤
 * `MAX_TRANSLATION_SOURCE_CHARS`), không nhận cả tệp, không đọc tệp từ S3.
 */
/** Ngôn ngữ đích — mã BCP-47 ngắn. Thứ tự là thứ tự hiện trong menu chọn. */
export const TRANSLATION_LANGS = [
    'vi',
    'en',
    'ja',
    'ko',
    'zh-Hans',
    'fr',
    'de',
    'th',
    'es',
];
export const TranslationLang = z.enum(TRANSLATION_LANGS);
/** Thể loại tài liệu nhận ra được — plan §3.3. */
export const DOCUMENT_GENRES = [
    'administrative',
    'legal',
    'technical',
    'business',
    'academic',
    'marketing',
    'meeting',
    'casual',
];
export const DocumentGenre = z.enum(DOCUMENT_GENRES);
/** Văn phong dịch — danh sách cố định; nhãn + mô tả hiện cho người dùng nằm ở i18n của web. */
export const TRANSLATION_STYLES = [
    'formal-administrative',
    'legal-precise',
    'technical-precise',
    'business-professional',
    'academic',
    'marketing-fluent',
    'neutral',
    'conversational',
];
export const TranslationStyle = z.enum(TRANSLATION_STYLES);
/** Thể loại → văn phong mặc định, 1–1 (plan §3.3). */
export const GENRE_DEFAULT_STYLE = {
    administrative: 'formal-administrative',
    legal: 'legal-precise',
    technical: 'technical-precise',
    business: 'business-professional',
    academic: 'academic',
    marketing: 'marketing-fluent',
    meeting: 'neutral',
    casual: 'conversational',
};
/** Văn phong khi chưa biết gì về tài liệu. */
export const DEFAULT_TRANSLATION_STYLE = 'neutral';
/** Trần chữ một khối gửi lên (luật #3 — chỉ một khối, không cả tệp). */
export const MAX_TRANSLATION_SOURCE_CHARS = 8_000;
/** Trần bản dịch lưu được. Đích có thể dài hơn nguồn (vi dài hơn en ~25 %), nên rộng tay. */
export const MAX_TRANSLATION_TEXT_CHARS = 32_000;
/** Trần đường tiêu đề chứa khối, gửi kèm để dịch đúng ngữ cảnh. */
export const MAX_TRANSLATION_CONTEXT_CHARS = 300;
/** Trần dàn ý tài liệu (tiêu đề, các tiêu đề mục, vài đoạn đầu) cho bước nhận ra văn phong. */
export const MAX_GENRE_OUTLINE_CHARS = 4_000;
/** Trần lời giải thích của bước nhận ra (hiện dưới cột dịch). */
export const MAX_GENRE_REASON_CHARS = 140;
/**
 * Số bản dịch giữ lại cho mỗi **chuỗi sửa của một đoạn** + ngôn ngữ: cùng vị trí khối và chữ gốc
 * đủ giống nhau (`sourceSimilarity` ≥ `SOURCE_SIMILAR_THRESHOLD`) — bản cũ để hiện "đoạn gốc đã
 * đổi". Bản của một đoạn **khác** từng đứng ở cùng vị trí (chèn/xoá khối phía trên làm trượt
 * `idx:<n>`) không bị dọn theo (APM-BIL-qa).
 */
export const TRANSLATION_KEEP_PER_BLOCK = 3;
/**
 * Ngưỡng "cùng một đoạn, đã sửa" — APM-BIL-qa. Dưới ngưỡng thì bản dịch cũ **không** được gắn
 * cho đoạn mới (không hiện "Đoạn gốc đã đổi" với bản dịch của một đoạn khác) và không bị dọn
 * cùng chuỗi với nó.
 *
 * 0.6 trên hệ số Dice của cặp ký tự (đo 2026-10-13): sửa một con số, một từ, thêm một mệnh đề vào đoạn
 * ở 0.65–0.95; hai câu khác nhau cùng tiếng Việt quanh 0.3–0.45 (cặp "ng", "nh", "th" chung nhiều);
 * và Dice ≤ 2·ngắn/(ngắn+dài), nên đoạn dài gấp ≥ 2.4 lần đoạn kia không bao giờ qua ngưỡng — đoạn
 * dài không mượn bản dịch của đoạn ngắn. Test: `document-translation.spec.ts`.
 */
export const SOURCE_SIMILAR_THRESHOLD = 0.6;
/** Chữ và số viết thường, NFC, mọi thứ khác (markdown, dấu câu) thành một dấu cách. */
function similarityText(text) {
    return text
        .normalize('NFC')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}
export function bigramBag(text) {
    const normalized = similarityText(text);
    const chars = [...normalized];
    const counts = new Map();
    for (let i = 0; i + 1 < chars.length; i++) {
        const pair = chars[i] + chars[i + 1];
        counts.set(pair, (counts.get(pair) ?? 0) + 1);
    }
    return { counts, size: Math.max(0, chars.length - 1), text: normalized };
}
/** Hệ số Dice của hai túi cặp ký tự: 1 = giống hệt, 0 = không chung cặp nào. */
export function diceOf(a, b) {
    if (a.size === 0 || b.size === 0)
        return a.text === b.text && a.text !== '' ? 1 : 0;
    const [small, large] = a.counts.size <= b.counts.size ? [a, b] : [b, a];
    let shared = 0;
    for (const [pair, count] of small.counts) {
        shared += Math.min(count, large.counts.get(pair) ?? 0);
    }
    return (2 * shared) / (a.size + b.size);
}
/** Độ giống của hai chữ gốc (0–1) — hàm thuần dùng chung web ↔ API. */
export function sourceSimilarity(a, b) {
    return diceOf(bigramBag(a), bigramBag(b));
}
export const TRANSLATE_DOCUMENT_BLOCK_SKILL = 'translate_document_block';
export const DETECT_DOCUMENT_GENRE_SKILL = 'detect_document_genre';
/**
 * Tài liệu đang mở, theo cách web biết nó:
 *
 * - `file` — tệp cá nhân của **chính người gọi**; `docKey` = id tệp đang mở (phiên bản nào cũng
 *   được — máy chủ quy về nhóm phiên bản).
 * - `shared` — tài liệu người khác chia sẻ cho người gọi; `docKey` = id lượt cấp quyền
 *   (`grantId` của "Được chia sẻ với tôi"). Lưu chung với bản dịch của chủ tệp.
 * - `document` — tài liệu dự án; `docKey` = id tài liệu.
 *
 * Trong bảng chỉ có `file` / `document`: `shared` là đường **vào**, không phải loại tài liệu.
 */
export const TranslationDocKind = z.enum(['file', 'shared', 'document']);
const DocRef = {
    docKind: TranslationDocKind,
    docKey: z.uuid(),
};
/** Băm khối do web tính (16 ký tự hex thường). */
export const TranslationSourceHash = z.string().regex(/^[0-9a-f]{16}$/);
/** Vị trí khối: `anchorId` của tiêu đề, hoặc `idx:<thứ tự khối cấp cao>`. */
export const TranslationBlockKey = z.string().trim().min(1).max(64);
const SourceBlock = {
    ...DocRef,
    lang: TranslationLang,
    sourceHash: TranslationSourceHash,
    blockKey: TranslationBlockKey,
    sourceText: z.string().trim().min(1).max(MAX_TRANSLATION_SOURCE_CHARS),
};
export const DocumentTranslationQuery = z.object({ ...DocRef, lang: TranslationLang });
/** `PUT /translations` — bản **tự dịch**. `style` = văn phong đang chọn lúc lưu. */
export const SaveDocumentTranslationInput = z.object({
    ...SourceBlock,
    text: z.string().trim().min(1).max(MAX_TRANSLATION_TEXT_CHARS),
    style: TranslationStyle.optional(),
});
/** Một khối trong lượt dịch AI — chữ, vị trí, băm, đường tiêu đề chứa nó. */
export const TranslateBlockItem = z.object({
    sourceHash: TranslationSourceHash,
    blockKey: TranslationBlockKey,
    sourceText: z.string().trim().min(1).max(MAX_TRANSLATION_SOURCE_CHARS),
    context: z.string().trim().max(MAX_TRANSLATION_CONTEXT_CHARS).optional(),
});
/**
 * `POST /translations/ai` — dịch **một trang** (1..N khối) bằng AI trong một lượt (APM-BIL-page,
 * plan §3.4). Web gom khối theo `TRANSLATION_PAGE_WEIGHT`; "Dịch lại" một khối gửi đúng một khối.
 *
 * Luật #3 vẫn nguyên: tổng chữ cả lô ≤ `MAX_TRANSLATION_SOURCE_CHARS` — cùng trần với một khối,
 * không bao giờ cả tệp. Hai khối cùng băm trong một lô là lỗi của bên gọi (một bản dịch dùng chung
 * cho cả hai). Bỏ trống `style` ⇒ văn phong của tài liệu.
 */
export const TranslateDocumentBlocksInput = z
    .object({
    ...DocRef,
    lang: TranslationLang,
    blocks: z.array(TranslateBlockItem).min(1).max(MAX_TRANSLATION_BATCH_BLOCKS),
    style: TranslationStyle.optional(),
    /** Mã dấu vết để mở bảng theo dõi trước khi POST (APM-1302). */
    runId: z.uuid().optional(),
})
    .superRefine((input, ctx) => {
    const total = input.blocks.reduce((sum, block) => sum + block.sourceText.length, 0);
    if (total > MAX_TRANSLATION_SOURCE_CHARS) {
        ctx.addIssue({
            code: 'custom',
            path: ['blocks'],
            message: `Tổng chữ một lượt dịch tối đa ${MAX_TRANSLATION_SOURCE_CHARS} ký tự`,
        });
    }
    const seen = new Set();
    input.blocks.forEach((block, index) => {
        if (seen.has(block.sourceHash)) {
            ctx.addIssue({
                code: 'custom',
                path: ['blocks', index, 'sourceHash'],
                message: 'Hai khối cùng nội dung trong một lượt dịch',
            });
        }
        seen.add(block.sourceHash);
    });
});
export const DocumentTranslationView = z.object({
    id: z.uuid(),
    sourceHash: TranslationSourceHash,
    blockKey: z.string(),
    /** Chữ gốc lúc dịch — để hiện "trước đây gốc là…" khi đoạn đã đổi. */
    sourceText: z.string(),
    text: z.string(),
    source: z.enum(['ai', 'manual']),
    status: z.enum(['ai_suggested', 'manual']),
    style: TranslationStyle.nullable(),
    createdBy: z.object({ id: z.uuid(), name: z.string() }),
    createdAt: z.iso.datetime(),
});
/** Mọi bản của tài liệu + ngôn ngữ, mới nhất trước; web tự khớp theo băm rồi theo vị trí. */
export const DocumentTranslationList = z.object({ items: z.array(DocumentTranslationView) });
/**
 * Kết cục của **một khối** trong lượt dịch: bản đã lưu, hoặc lỗi của riêng khối ấy (khối đã ở đúng
 * ngôn ngữ đích, model trả thiếu / hỏng khối này…) — các khối khác của lô vẫn lưu.
 */
export const TranslatedBlockOutcome = z.object({
    sourceHash: TranslationSourceHash,
    blockKey: z.string(),
    translation: DocumentTranslationView.nullable(),
    error: z.object({ code: z.string(), message: z.string() }).nullable(),
});
/** `POST /translations/ai` — mỗi khối một kết cục, đúng thứ tự gửi lên; kèm trần đã chọn (plan §3.2). */
export const TranslateDocumentBlocksResult = z.object({
    items: z.array(TranslatedBlockOutcome),
    costUsd: z.string(),
    /** Trần output token của lượt cuối (đã nhân đôi nếu lượt đầu bị cắt); `0` = không gọi model. */
    maxOutputTokens: z.number().int().nonnegative(),
});
// ── Văn phong (plan §3.3) ──────────────────────────────────────────────────
export const TranslationProfileQuery = z.object(DocRef);
/** Chưa có hàng ⇒ mọi trường `null` — một giá trị, không phải thân rỗng. */
export const TranslationProfileView = z.object({
    style: TranslationStyle.nullable(),
    styleSource: z.enum(['auto', 'manual']).nullable(),
    genre: DocumentGenre.nullable(),
    detectedAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime().nullable(),
});
/** `PUT /translations/profile` — người dùng đổi văn phong (`styleSource: 'manual'`). */
export const SetTranslationStyleInput = z.object({ ...DocRef, style: TranslationStyle });
/** `POST /translations/profile/detect` — dàn ý do web dựng: tiêu đề, các tiêu đề mục, vài đoạn đầu. */
export const DetectDocumentGenreInput = z.object({
    ...DocRef,
    outline: z.string().trim().min(1).max(MAX_GENRE_OUTLINE_CHARS),
    runId: z.uuid().optional(),
    /**
     * `true` = người dùng **bấm** "Nhận ra lại" (APM-BIL-qa): áp văn phong vừa nhận ra và chuyển
     * nguồn về `auto`, kể cả khi đang là văn phong chọn tay. Bỏ trống = lượt tự chạy khi mở Song
     * ngữ lần đầu — không đè lựa chọn tay.
     */
    redetect: z.boolean().optional(),
});
/** Output của skill `detect_document_genre`. */
export const DocumentGenreDetection = z.object({
    genre: DocumentGenre,
    style: TranslationStyle,
    confidence: z.number().min(0).max(1),
    reason: z.string().trim().min(1).max(MAX_GENRE_REASON_CHARS),
});
export const DetectDocumentGenreResult = z.object({
    profile: TranslationProfileView,
    detection: z.object({
        /** `null` = quy tắc không nhận ra và không có AI ⇒ văn phong trung tính. */
        genre: DocumentGenre.nullable(),
        style: TranslationStyle,
        confidence: z.number().min(0).max(1),
        /** `rules` = bộ quy tắc từ khoá tất định; `ai` = skill `detect_document_genre`. */
        method: z.enum(['rules', 'ai']),
        reason: z.string(),
    }),
});
/** Trần mã khối trong lượt dịch (`b1`…`b40`) — model chép lại đúng mã. */
const TRANSLATED_ID = z.string().trim().min(1).max(8);
/**
 * Output của skill `translate_document_block`: mỗi khối một bản dịch, theo mã khối của lượt
 * (`b1`, `b2`… do máy chủ đặt, không phải `blockKey`) — APM-BIL-page.
 */
export const TranslatedBlocks = z.object({
    translations: z
        .array(z.object({
        id: TRANSLATED_ID,
        text: z.string().trim().min(1).max(MAX_TRANSLATION_TEXT_CHARS),
    }))
        .min(1)
        .max(MAX_TRANSLATION_BATCH_BLOCKS),
});
//# sourceMappingURL=document-translation.js.map